import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environments';
import { AUTH_SKIP_INTERCEPTOR, AUTH_SUPPRESS_401_REDIRECT } from './auth.interceptor';

export interface LoginCredentials {
    username: string;
    password: string;
}

interface KeycloakTokenResponse {
    access_token?: string;
}

export const AUTH_TOKEN_STORAGE_KEY = 'ArToken';
export const AUTH_USERINFO_STORAGE_KEY = 'ArUserInfo';

export const getStoredAuthToken = (): string | null => localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
export const getStoredUserInfo = (): string | null => localStorage.getItem(AUTH_USERINFO_STORAGE_KEY);

export const clearStoredAuthToken = (): void => localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
export const clearStoredUserInfo = (): void => localStorage.removeItem(AUTH_USERINFO_STORAGE_KEY);

export const clearStoredAuthSession = (): void => {
    clearStoredAuthToken();
    clearStoredUserInfo();
};

const setStoredAuthToken = (token: string): void => {
    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
};

const setStoredUserInfo = (userInfo: unknown): void => {
    localStorage.setItem(AUTH_USERINFO_STORAGE_KEY, JSON.stringify(userInfo));
};

const KEYCLOAK_REALM_BASE_URL = environment.keycloackURL;
const KEYCLOAK_TOKEN_ENDPOINT = `${KEYCLOAK_REALM_BASE_URL}/protocol/openid-connect/token`;
const KEYCLOAK_USERINFO_ENDPOINT = `${KEYCLOAK_REALM_BASE_URL}/protocol/openid-connect/userinfo`;
const KEYCLOAK_CLIENT_ID = 'ARIEL_PWA';
const MANAGER_BASE_URL = environment.managerURL;

@Injectable()
export class AuthService {

    constructor(
        private http: HttpClient,
        private router: Router,
    ) {}

    login(credentials: LoginCredentials): Observable<void> {
        const body = new URLSearchParams({
            client_id: KEYCLOAK_CLIENT_ID,
            grant_type: 'password',
            scope: 'openid',
            username: credentials.username,
            password: credentials.password,
        }).toString();

        return this.http
            .post<KeycloakTokenResponse>(KEYCLOAK_TOKEN_ENDPOINT, body, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/x-www-form-urlencoded',
                }),
                context: new HttpContext()
                    .set(AUTH_SKIP_INTERCEPTOR, true)
                    .set(AUTH_SUPPRESS_401_REDIRECT, true),
            })
            .pipe(
                tap((response) => {
                    if (!response.access_token) {
                        throw new Error('Missing access_token in Keycloak response.');
                    }

                    setStoredAuthToken(response.access_token);
                }),
                switchMap(() => this.loadUserInfo()),
                map(() => void 0),
            );
    }

    recoverPassword(email: string): Observable<void> {
        return this.http.post<void>(`${MANAGER_BASE_URL}/users/forgot-password`, { email });
    }

    logout(): void {
        clearStoredAuthSession();
        void this.router.navigate(['/login']);
    }

    getAccessToken(): string | null {
        return getStoredAuthToken();
    }

    getName() {
        const stored = getStoredUserInfo();

        if (!stored) {
            return '';
        }

        const userInfo = JSON.parse(stored);
        return userInfo.name;

    }

    validateSession(): Observable<boolean> {
        if (!this.getAccessToken()) {
            return of(false);
        }

        return this.http
            .get(KEYCLOAK_USERINFO_ENDPOINT, {
                context: new HttpContext().set(AUTH_SUPPRESS_401_REDIRECT, true),
            })
            .pipe(
                tap((userInfo) => setStoredUserInfo(userInfo)),
                map(() => true),
                catchError(() => {
                    clearStoredAuthSession();
                    return of(false);
                }),
            );
    }

    private loadUserInfo(): Observable<unknown> {
        return this.http
            .get(KEYCLOAK_USERINFO_ENDPOINT, {
                context: new HttpContext().set(AUTH_SUPPRESS_401_REDIRECT, true),
            })
            .pipe(tap((userInfo) => setStoredUserInfo(userInfo)));
    }
}
